import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Checkbox, Modal, Tooltip, message } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { find } from 'lodash-es';
import StatusButton from 'components/StatusButton';
import { useAuthorization } from 'components/Authorization';
import { OnOrder, subscriptionStatus } from 'services/api/pay';
import PackageDlgDetail from './PackageDlgDetail';
import { STRIPE_SUBSCRIPTION_RESULT_TYPE } from './StripeSubscriptionModal';
import { canPayAirwallex } from './Package';
import PayWithSubWindow from 'bizComponents/pay/PayWithSubWindow';
import usePayPackage from '../hooks/usePayPackage';
import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import { EnterpriseTotal } from './EnterpriseTotal';
import { NonEnterpriseTotal, NonEnterpriseTotalProps } from './NonEnterpriseTotal';
import { PackageTimePanel } from './PackageTimePanel';
import { isQuickChat } from 'productLine';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { refreshBotUsageSummary } from 'hooks/business/system/useBot';
import { refreshComboInfo } from '../hooks/useComboInfo';
import { useQueryClient } from '@tanstack/react-query';
import { org } from 'services/api/admin';
import { useSetOrgValue } from 'hooks/business/system/useOrg';
import { CompanyAliHelp, Help } from './OrderAI/Help';
import wait from 'utils/wait';
import TransComponent from 'components/TransComponent';
import { currencySymbol } from './PriceText';
import { isPackageCurrencyLocked, resolvePackageCurrency } from '../packageCurrency';

export interface PackageProps {
  orderType: Pay.OrderType;
  orgId: number;
  orderedPackages: Pay.OrderedPackage[];
  packageInfo: Pay.PackageInfoV2;
  /** 订阅套餐升级时沿用原订阅币种 */
  lockedCurrency?: General.Currency;
  onOrder: OnOrder;
  /** 升级订阅成功后回调（用于关闭弹窗） */
  onSuccess?: () => void;
}

// 升级/续订套餐
function PackageDlg(props: PackageProps) {
  const { onOrder, onSuccess, packageInfo, orderedPackages, orgId, orderType, lockedCurrency } =
    props;

  const { t } = useTranslate();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { accid = '' } = payload || {};

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;

  const {
    // 是否企业套餐
    isEnterprise,
    currenciesOptions,
    availablePayTypes,
    currency: selectedCurrency,
    setCurrency,
    payType,
    setPayType,
    setIsEnterprise,
    bank,
    setBank,
  } = usePayPackage();
  const currency = resolvePackageCurrency({
    selectedCurrency,
    lockedCurrency,
    isEnterprise,
    currenciesOptions,
  });
  const currencyLocked = isPackageCurrencyLocked(lockedCurrency, isEnterprise);

  const language = useSystemLanguageValue();
  const [accountNum, setAccountNum] = useState(0);
  const [durationNum, setDurationNum] = useState<Pay.DurationNum | -1>(0);
  const [canOrder, setCanOrder] = useState(false);
  // 选择的套餐id
  const [checkedPackageId, setCheckedPackageId] = useState<Pay.PackageId | undefined>(
    recommendPackageId,
  );
  const [calculateRst, setCalculateRst] = useState<Pay.CalculatePackagePriceRst | undefined>();

  // 当前用户是否为订阅用户（连续包月）
  const isWorkingSubscription = orderedPackages.find((v) => v.status === 1)?.autoRenewing === true;
  // 是否选择自动续费（连续包月）订阅：
  // 订阅用户升级默认保持订阅；续订默认连续包月
  const [isSubscription, setIsSubscription] = useState(
    orderType === 'UPGRADE' ? isWorkingSubscription : orderType === 'RENEW',
  );
  const [ordering, setOrdering] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  // 升级订阅需要支付验证时的状态
  const [stripePayUrl, setStripePayUrl] = useState('');
  const [stripeUpgradeVisible, setStripeUpgradeVisible] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      // 首次查询是 setTimeout，后续轮询是 setInterval，两种 id 都清理
      clearTimeout(pollingRef.current);
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    // 轮询结束（成功/取消/卸载）时恢复按钮
    setOrdering(false);
  }, []);

  const queryClient = useQueryClient();
  const setOrgValue = useSetOrgValue();

  const successRef = useRef(false);

  const handleUpgradeSuccess = useCallback(() => {
    if (successRef.current) {
      return;
    }
    successRef.current = true;
    message.success(t('扣款成功-邮件-主题', { X: t(`套餐.${checkedPackageId}`) }));
    // 刷新顶部套餐信息与订单数据
    // org 接口有缓存延迟，等 1 秒再拉取（与 Market.refreshAfterPay 一致）
    wait(1000).then(() => {
      org()
        .then(setOrgValue)
        .catch(() => {});
    });
    // Profile 的套餐名来自 orderedPackage、AI 报告等来自 lightWeightPackageInfo
    queryClient.invalidateQueries({ queryKey: ['accounts', 'orderedPackage'] });
    queryClient.invalidateQueries({ queryKey: ['accounts', 'lightWeightPackageInfo'] });
    refreshComboInfo();
    refreshBotUsageSummary();
    onSuccess?.();
  }, [onSuccess, queryClient, setOrgValue, t, checkedPackageId]);

  // 轮询订阅状态，直到套餐和帐号数都与目标订阅一致
  const startPolling = useCallback(
    (targetPackageId: Pay.PackageId, targetAccountNum: number) => {
      stopPolling();
      // 轮询期间按钮保持 loading
      setOrdering(true);
      const check = () => {
        // 不传 subscriptionId，查询当前生效订阅
        subscriptionStatus()
          .then((res) => {
            if (res.packageId === targetPackageId && res.accountNum === targetAccountNum) {
              stopPolling();
              setStripeUpgradeVisible(false);
              setStripePayUrl('');
              handleUpgradeSuccess();
            }
          })
          .catch(() => {
            // 轮询失败忽略，继续轮询
          });
      };
      // 下单成功后等 1 秒再首次查询（后端状态缓存延迟），之后每 3 秒轮询
      pollingRef.current = setTimeout(() => {
        check();
        pollingRef.current = setInterval(check, 3000);
      }, 1000);
    },
    [handleUpgradeSuccess, stopPolling],
  );

  // 组件卸载时停止轮询
  useEffect(() => stopPolling, [stopPolling]);

  // 监听 Stripe 结果页 postMessage，支付成功/取消立即结束支付验证流程
  // （轮询 subscriptionStatus 作为兜底，防止 postMessage 丢失）
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const { type, result } = e.data || {};
      if (type !== STRIPE_SUBSCRIPTION_RESULT_TYPE) {
        return;
      }
      stopPolling();
      setStripeUpgradeVisible(false);
      setStripePayUrl('');
      if (result === 'success') {
        handleUpgradeSuccess();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleUpgradeSuccess, stopPolling]);

  // 当前生效的套餐
  const curWorkingPackage = useMemo(
    () => orderedPackages.find((v) => v.status === 1),
    [orderedPackages],
  );

  // 实付价格
  const actualPrice = useMemo(() => calculateRst?.payPrice ?? 0, [calculateRst?.payPrice]);

  useEffect(() => {
    // 连续包月仅支持 Stripe 支付
    if (isSubscription && payType !== 'stripe') {
      setPayType('stripe');
    }
  }, [isSubscription, payType, setPayType]);

  useEffect(() => {
    if (!isSubscription) {
      setAgreementChecked(false);
    }
  }, [isSubscription]);

  const displayedPayTypes = useMemo(() => {
    if (isSubscription) {
      return ['stripe' as const];
    }
    return availablePayTypes;
  }, [isSubscription, availablePayTypes]);

  const fetchSubscriptionUpgrade = useCallback(() => {
    const origin =
      import.meta.env.VITE_ENV === 'production'
        ? 'https://mai.zhisuitech.com'
        : 'https://feature-stq-kjds-mai.ftest.leyantech.com';
    // 订阅升级走老订购接口，subscription=true + durationNum=-1，
    // Stripe 支付验证地址由返回的 paymentUrl 提供
    return onOrder({
      idempotent: `${Date.now()}`,
      accid,
      packageType: isQuickChat ? 3 : 1,
      accountNum,
      durationNum: -1,
      discountTotalPrice: `${actualPrice}`,
      orderAction: orderType,
      payType: 'stripe',
      currency,
      packageId: checkedPackageId!,
      language,
      bank,
      subscription: true,
      payResultUrl: `${origin}/#/stripe-subscription-result?result=success`,
      cancelResultUrl: `${origin}/#/stripe-subscription-result?result=cancel`,
    })
      .then((res) => {
        const { paymentUrl } = res ?? {};
        // 有 paymentUrl 时打开支付验证弹窗；
        // 没有（如已绑定支付方式直接扣款）时不打开弹窗
        setStripePayUrl(paymentUrl ?? '');
        setStripeUpgradeVisible(!!paymentUrl);
        if (orderType === 'UPGRADE') {
          // 升级会更换套餐，轮询订阅状态直到 packageId/accountNum 变为目标
          startPolling(checkedPackageId!, accountNum);
        } else if (!paymentUrl) {
          // 续订不改变套餐，无需轮询（轮询条件天然成立会误判）；
          // 未返回支付地址视为已直接扣款成功
          setOrdering(false);
          handleUpgradeSuccess();
        }
      })
      .catch(() => {
        setOrdering(false);
      });
  }, [
    accid,
    accountNum,
    actualPrice,
    bank,
    checkedPackageId,
    currency,
    handleUpgradeSuccess,
    language,
    onOrder,
    orderType,
    startPolling,
  ]);

  // 总计价格
  const totalPrices: NonEnterpriseTotalProps['totalPrices'] = useMemo(() => {
    return {
      actualPrice: calculateRst?.payPrice ?? 0,
      reducePrice: calculateRst?.reducePrice ?? 0,
    };
  }, [calculateRst]);

  useMount(() => {
    const recommendPackage = find(packageData, ['id', recommendPackageId]);
    if (recommendPackage) {
      setAccountNum(recommendPackage.accountNum);
      const recommendDuration = find(recommendPackage.durationDesc, ['id', recommendDurationId]);
      setDurationNum(recommendDuration?.durationNum ?? 0);
    }
  });

  let orderElement = null;
  if (isEnterprise) {
    // 企业版套餐信息
    orderElement = <EnterpriseTotal currency={currency} />;
  } else if (canOrder) {
    orderElement = (
      <>
        <NonEnterpriseTotal currency={currency} totalPrices={totalPrices}>
          {isSubscription ? (
            <SubscriptionInfo>
              <div>
                {t('自动续费')}：{t('phrase.已开启')}
              </div>
              <div>
                {t('下次扣费')}：{calculateRst?.afterValidEnd}
              </div>
            </SubscriptionInfo>
          ) : (
            calculateRst &&
            curWorkingPackage && (
              <PackageTimePanel
                orderType={orderType}
                curWorkingPackage={curWorkingPackage}
                data={calculateRst}
                currency={currency}
                checkedPackageId={checkedPackageId}
                // 非订阅分支才会渲染，时长不可能是 -1
                durationNum={durationNum as Pay.DurationNum}
              />
            )
          )}
        </NonEnterpriseTotal>
        {isSubscription && (
          <AgreementLine>
            <Checkbox
              checked={agreementChecked}
              onChange={(e) => setAgreementChecked(e.target.checked)}
            >
              <T
                keyName="订购代表接受x"
                params={{
                  x: (
                    <TransComponent
                      content={
                        <Tooltip
                          title={
                            <div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>
                          }
                        >
                          <AgreementText>{t('自动续费协议')}</AgreementText>
                        </Tooltip>
                      }
                    />
                  ),
                }}
              />
            </Checkbox>
          </AgreementLine>
        )}
        <OrderButton
          type="primary"
          size="large"
          loading={ordering}
          onClick={() => {
            if (payType === 'airwallex' && !canPayAirwallex()) {
              Modal.info({
                title: t('market.当前客户端版本不支持 Airwallex 支付'),
                content: t('market.请重启客户端，升级至最新版本来完成订购'),
              });

              return Promise.resolve();
            }

            if (isSubscription) {
              if (!agreementChecked) {
                setConfirmModalVisible(true);
                return Promise.resolve();
              }
              setOrdering(true);
              // loading 由轮询 startPolling/stopPolling 控制，成功后弹窗关闭
              return fetchSubscriptionUpgrade();
            }

            return onOrder({
              idempotent: `${Date.now()}`,
              accid,
              packageType: isQuickChat ? 3 : 1,
              accountNum,
              durationNum,
              discountTotalPrice: `${totalPrices.actualPrice}`,
              orderAction: orderType,
              payType,
              currency,
              packageId: checkedPackageId!,
              language,
              bank,
            });
          }}
        >
          {orderType === 'UPGRADE' ? t('market.立即升级') : t('market.立即续订')}
        </OrderButton>
      </>
    );
  }
  return (
    <Container>
      <Content>
        <PackageDlgDetail
          orderType={orderType}
          orgId={orgId}
          currency={currency}
          orderedPackages={orderedPackages}
          packageInfo={packageInfo}
          checkedPackageId={checkedPackageId}
          curWorkingPackage={curWorkingPackage}
          setAccountNum={setAccountNum}
          setCanOrder={setCanOrder}
          isEnterprise={isEnterprise}
          setIsEnterprise={setIsEnterprise}
          onPackagePriceChange={setCalculateRst}
          onPackageChange={setCheckedPackageId}
          onDurationChange={setDurationNum}
          setIsSubscription={setIsSubscription}
        />
        <CurrencyCheckbox
          currencies={currenciesOptions}
          value={currency}
          onChange={setCurrency}
          currencyDisabled={(value) => currencyLocked && value !== lockedCurrency}
        />
        {isEnterprise ? null : (
          <PayTypeCheckbox
            payTypes={displayedPayTypes}
            // 连续包月仅支持 Stripe，且 stripe 不在货币过滤的 availablePayTypes 中
            // （usePayTypeState 会把 payType 覆盖回列表首位），这里直接强制勾选 stripe
            value={isSubscription ? 'stripe' : payType}
            onChange={setPayType}
            currency={currency}
            bank={bank}
            onBankChange={setBank}
          />
        )}
        {currency === 'CNY' && (
          <div className="mt-[-12px]">
            <span className="mr-[6px]">
              {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
            </span>
            <CompanyAliHelp />
            <Help />
          </div>
        )}
        {orderElement}
        <Modal
          visible={confirmModalVisible}
          closable={false}
          width={400}
          onOk={() => {
            setConfirmModalVisible(false);
            setAgreementChecked(true);
            setOrdering(true);
            // loading 由轮询 startPolling/stopPolling 控制，成功后弹窗关闭
            fetchSubscriptionUpgrade();
          }}
          onCancel={() => setConfirmModalVisible(false)}
          okText={t('确认订购')}
          cancelText={t('translation.取消')}
        >
          <T
            keyName="确认订购tips"
            params={{
              x: (
                <TransComponent
                  content={
                    <Tooltip
                      title={<div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>}
                    >
                      <AgreementText>{t('自动续费协议')}</AgreementText>
                    </Tooltip>
                  }
                />
              ),
              y: t(`套餐.${checkedPackageId}`),
              price: `${currencySymbol[currency]} ${actualPrice}`,
            }}
          />
        </Modal>
        {stripeUpgradeVisible && (
          <Modal
            visible={stripeUpgradeVisible}
            title={t(orderType === 'UPGRADE' ? 'market.升级套餐' : 'market.续订套餐')}
            width={480}
            footer={null}
            onCancel={() => {
              stopPolling();
              setStripeUpgradeVisible(false);
              setStripePayUrl('');
            }}
          >
            <UpgradeVerifyContent>
              <SyncOutlined spin style={{ fontSize: 32, color: 'var(--fett-app-primary-color)' }} />
              <PayWithSubWindow
                src={stripePayUrl}
                name="STRIPE_SUBSCRIPTION_UPGRADE_WINDOW"
                expired={false}
                onRefresh={() => Promise.resolve()}
              />
            </UpgradeVerifyContent>
          </Modal>
        )}
      </Content>
    </Container>
  );
}

export default PackageDlg;

const Container = styled.div`
  display: flex;
`;

const Content = styled.div`
  flex: 1 0 0;
  /* margin-right: 280px; */
  > * {
    margin-bottom: 20px;
  }
  > h2 {
    margin-bottom: 12px;
    font-size: 20px;
    color: var(--fett-text-primary);
  }
`;

const OrderButton = styled(StatusButton)`
  width: 260px;
  &,
  &:hover,
  &:active,
  &:focus {
    background-color: var(--fett-app-primary-color);
    border: none;
  }
`;

const SubscriptionInfo = styled.div`
  color: var(--fett-text-tertiary);
  > div + div {
    margin-top: 4px;
  }
`;

const AgreementLine = styled.div`
  margin-top: -12px;
`;

const AgreementText = styled.span`
  margin: 0 4px;
  color: var(--fett-app-primary-color);
  cursor: pointer;
`;

const UpgradeVerifyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: var(--fett-text-secondary);
`;
